import { useState, useRef } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { MaskedLines, Fade, Overline } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CONTACT_AREAS } from "@/data/content";

const inputClass =
  "w-full bg-transparent border-b border-ink/25 py-3 text-base text-ink placeholder:text-ink/35 focus:outline-none focus:border-burgundy transition-colors duration-300";

const Field = ({ label, required, error, children, testid }) => (
  <div data-testid={testid}>
    <label className="block text-xs uppercase tracking-[0.2em] font-medium text-ink/60 mb-1">
      {label} {required && <span className="text-burgundy">*</span>}
    </label>
    {children}
    {error && <p className="mt-2 text-xs text-burgundy" data-testid={`${testid}-error`}>{error}</p>}
  </div>
);

const Contact = () => {
  const [form, setForm] = useState({
    name: "", organization: "", email: "", phone: "",
    area_of_interest: "", message: "", consent: false, website: "",
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const mountedAt = useRef(Date.now());

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const validate = () => {
    const errs = {};
    if (form.name.trim().length < 2) errs.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    if (!form.area_of_interest) errs.area_of_interest = "Please select an area of interest.";
    if (form.message.trim().length < 10) errs.message = "Please tell us a little more (at least 10 characters).";
    if (!form.consent) errs.consent = "Consent is required so we can respond to your inquiry.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await api.post("/contact", { ...form, elapsed_ms: Date.now() - mountedAt.current });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact | Mosaicon Strategies — Start a Conversation"
        description="Tell us about your organization, project, opportunity or challenge. Contact Mosaicon Strategies in the Greater Toronto Area at 416.568.4178 or mku@mosaicons.ca."
      />

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24 md:pt-28 md:pb-32" data-testid="contact-page">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-5">
            <Overline>Contact</Overline>
            <h1 className="mt-6 font-serif text-5xl md:text-6xl tracking-tighter text-ink leading-[1.05]" data-testid="contact-heading">
              <MaskedLines delay={0.15} lines={["Start a", <><em className="text-burgundy">conversation</em>.</>]} />
            </h1>
            <Fade delay={0.3} className="mt-8 space-y-5 text-base leading-relaxed text-ink/70 max-w-md" data-testid="contact-intro">
              <p>Every engagement begins with understanding what you are trying to accomplish.</p>
              <p>
                Tell us about your organization, project, opportunity or challenge and we will be pleased to
                explore how Mosaicon Strategies may be able to help.
              </p>
            </Fade>
            <Fade delay={0.4} className="mt-12 border-t border-ink/15 pt-10">
              <address className="not-italic space-y-5" data-testid="contact-details">
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-burgundy font-semibold">Office</p>
                  <p className="mt-2 text-base text-ink/80 leading-relaxed">
                    Mosaicon Strategies<br />Greater Toronto Area<br />Ontario, Canada
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-burgundy font-semibold">Phone</p>
                  <a href="tel:+14165684178" className="mt-2 block text-base text-ink/80 link-underline w-fit" data-testid="contact-phone">
                    416.568.4178
                  </a>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-burgundy font-semibold">Email</p>
                  <a href="mailto:mku@mosaicons.ca" className="mt-2 block text-base text-ink/80 link-underline w-fit" data-testid="contact-email">
                    mku@mosaicons.ca
                  </a>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-burgundy font-semibold">Web</p>
                  <a href="https://www.mosaicons.ca" className="mt-2 block text-base text-ink/80 link-underline w-fit" data-testid="contact-website">
                    www.mosaicons.ca
                  </a>
                </div>
              </address>
            </Fade>
          </div>

          <div className="lg:col-span-7">
            <Fade delay={0.25}>
              {sent ? (
                <div className="border border-ink/15 bg-white p-12 md:p-16 text-center" data-testid="contact-success">
                  <CheckCircle2 className="h-12 w-12 text-burgundy mx-auto" />
                  <h2 className="mt-8 font-serif text-3xl tracking-tight text-ink">Thank you.</h2>
                  <p className="mt-4 text-base leading-relaxed text-ink/70 max-w-md mx-auto">
                    Thank you for contacting Mosaicon Strategies. Your message has been received and we will
                    respond as soon as possible.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} noValidate className="border border-ink/15 bg-white p-8 md:p-12" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-x-8 gap-y-8">
                    <Field label="Name" required error={errors.name} testid="field-name">
                      <input
                        type="text" value={form.name} onChange={set("name")} className={inputClass}
                        placeholder="Your full name" autoComplete="name" data-testid="contact-name-input"
                      />
                    </Field>
                    <Field label="Organization" testid="field-organization">
                      <input
                        type="text" value={form.organization} onChange={set("organization")} className={inputClass}
                        placeholder="Company or organization" autoComplete="organization" data-testid="contact-organization-input"
                      />
                    </Field>
                    <Field label="Email" required error={errors.email} testid="field-email">
                      <input
                        type="email" value={form.email} onChange={set("email")} className={inputClass}
                        placeholder="you@example.com" autoComplete="email" data-testid="contact-email-input"
                      />
                    </Field>
                    <Field label="Phone" testid="field-phone">
                      <input
                        type="tel" value={form.phone} onChange={set("phone")} className={inputClass}
                        placeholder="416.000.0000" autoComplete="tel" data-testid="contact-phone-input"
                      />
                    </Field>
                  </div>
                  <div className="mt-8">
                    <Field label="Area of Interest" required error={errors.area_of_interest} testid="field-area">
                      <select
                        value={form.area_of_interest} onChange={set("area_of_interest")}
                        className={`${inputClass} cursor-pointer ${form.area_of_interest ? "text-ink" : "text-ink/35"}`}
                        data-testid="contact-area-select"
                      >
                        <option value="" disabled>Select an area of interest</option>
                        {CONTACT_AREAS.map((a) => (
                          <option key={a} value={a}>{a}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <div className="mt-8">
                    <Field label="Message" required error={errors.message} testid="field-message">
                      <textarea
                        value={form.message} onChange={set("message")} rows={5} className={`${inputClass} resize-y`}
                        placeholder="Tell us about your organization, project, opportunity or challenge."
                        data-testid="contact-message-input"
                      />
                    </Field>
                  </div>

                  {/* honeypot */}
                  <input
                    type="text" value={form.website} onChange={set("website")} tabIndex={-1} autoComplete="off"
                    aria-hidden="true" className="absolute opacity-0 h-0 w-0 pointer-events-none" name="website"
                  />

                  <div className="mt-8" data-testid="field-consent">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox" checked={form.consent} onChange={set("consent")}
                        className="mt-1 h-4 w-4 accent-[#610C27]"
                        data-testid="contact-consent-checkbox"
                      />
                      <span className="text-sm leading-relaxed text-ink/70">
                        I consent to Mosaicon Strategies contacting me regarding my inquiry.
                      </span>
                    </label>
                    {errors.consent && <p className="mt-2 text-xs text-burgundy" data-testid="consent-error">{errors.consent}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-btn"
                    className="mt-10 w-full md:w-auto bg-burgundy text-cream text-xs uppercase tracking-[0.22em] font-bold px-12 py-5 transition-colors duration-300 hover:bg-ink disabled:opacity-60 inline-flex items-center justify-center gap-3"
                  >
                    {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                    Send Inquiry
                  </button>
                  <p className="mt-6 text-xs leading-relaxed text-ink/45">
                    Your information is used only to respond to your inquiry. See our{" "}
                    <a href="/privacy" className="underline hover:text-burgundy">Privacy Policy</a>.
                  </p>
                </form>
              )}
            </Fade>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
